import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { NavLink, Outlet, Link, useLocation, useMatches, useNavigationType } from 'react-router-dom';
import { Icon, type IconName } from './icons';
import { Logo } from './Logo';
import { MachineStatusPill } from './MachineConnect';
import { ScaleStatusPill } from './ScaleConnect';
import { useFocusModeActive } from './focusMode';
import { applyNavCollapsed, readNavCollapsed } from './navCollapsed';
import { NavTooltip } from './NavTooltip';
import { announce, cx, documentTitle } from './ui';
import { MQ } from '@/lib/useMediaQuery';
import { useAiConfig } from '@/ai/config';
import { track, useSharingUsage } from '@/lib/usage';
import type { UsagePage } from '@shared/usage';

/** What a route's `handle` may say about it: `title` names the page in the browser tab. */
export interface RouteHandle {
  title?: string;
  /** The page, as a usage event names it (App.tsx sets it from the route's path). */
  page?: UsagePage;
}

interface NavEntry {
  to: string;
  label: string;
  icon: IconName;
  end?: boolean;
  /** The raised Brew button. */
  cta?: boolean;
  /** Left out of the phone tab bar, which fits five: Scale is the top bar's status pill, Settings and Ask its icons. */
  notPhone?: boolean;
  /** Only with AI set up, on this device or the server. */
  ai?: boolean;
}

/** One order on every screen size (tab bar left to right, rail top to bottom), Brew in the middle. */
const NAV: NavEntry[] = [
  { to: '/', label: 'Home', icon: 'home', end: true },
  { to: '/beans', label: 'Beans', icon: 'bean' },
  { to: '/scale', label: 'Scale', icon: 'scale', notPhone: true },
  { to: '/brew', label: 'Brew', icon: 'coffee', cta: true },
  { to: '/brews', label: 'History', icon: 'history' },
  { to: '/equipment', label: 'Gear', icon: 'grinder' },
  { to: '/ask', label: 'Ask', icon: 'sparkles', notPhone: true, ai: true },
  { to: '/settings', label: 'Settings', icon: 'settings', notPhone: true },
];

/**
 * Start each newly opened page at the top. Only pathname changes count (filters live in the query string),
 * back/forward is left to the browser, and #hash links are scrolled by the page itself.
 */
function useScrollToTopOnNavigate() {
  const { pathname, hash } = useLocation();
  const navType = useNavigationType();
  useEffect(() => {
    if (navType !== 'POP' && !hash) window.scrollTo(0, 0);
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps -- run on page changes only
}

/** The title of the deepest matched route that has one. */
export function routeTitle(matches: ReadonlyArray<{ handle: unknown }>): string | null {
  for (let i = matches.length - 1; i >= 0; i--) {
    const title = (matches[i]!.handle as RouteHandle | undefined)?.title;
    if (title) return title;
  }
  return null;
}

/**
 * Counts each page shown, by the pattern of its address (page_viewed, shared/usage.ts), while sharing is on. The page
 * the app opens on counts once the answer has loaded, which is after the page is shown.
 */
function usePageViews() {
  const matches = useMatches();
  const page = (matches.at(-1)?.handle as RouteHandle | undefined)?.page;
  const { pathname } = useLocation();
  const sharing = useSharingUsage();
  useEffect(() => {
    if (page && sharing) track('page_viewed', { page });
  }, [page, pathname, sharing]);
}

/**
 * Names the browser tab after the route ("History · Taretto"). A page that knows a better name (a bean's) sets it
 * with usePageTitle: this runs as a layout effect, before the page's effects, so the page's name wins.
 */
function useRouteTitle() {
  const title = routeTitle(useMatches());
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    document.title = documentTitle(title);
  }, [title, pathname]);
}

/**
 * After a page change, focus moves to the new page's h1, so a screen reader starts there and the next Tab goes into
 * the page. Not when something in the page already has focus (an autofocused field, a list link that stays put in
 * the split view), and not on the first load, where the skip link stays the first Tab stop. A lazily loaded page is
 * waited for; a page without an h1 (the guided brew) gets focus on <main> and its title announced.
 */
function useFocusPageOnNavigate() {
  const { pathname } = useLocation();
  const firstLoad = useRef(true);
  useEffect(() => {
    if (firstLoad.current) {
      firstLoad.current = false;
      return;
    }
    const main = document.getElementById('main');
    if (!main) return;
    const taken = () => {
      const active = document.activeElement;
      return active instanceof HTMLElement && active !== main && main.contains(active);
    };
    let done = false;
    const focusHeading = () => {
      if (done || taken()) return (done = true);
      const h1 = main.querySelector('h1');
      if (!h1) return false;
      if (!h1.hasAttribute('tabindex')) h1.setAttribute('tabindex', '-1');
      h1.setAttribute('data-route-focus', '');
      h1.focus({ preventScroll: true });
      return (done = true);
    };
    if (focusHeading()) return;
    const observer = new MutationObserver(() => {
      if (focusHeading()) stop();
    });
    const timer = setTimeout(() => {
      stop();
      if (done || taken()) return;
      done = true;
      main.focus({ preventScroll: true });
      announce(document.title);
    }, 2500);
    function stop() {
      observer.disconnect();
      clearTimeout(timer);
    }
    observer.observe(main, { childList: true, subtree: true });
    return () => {
      done = true;
      stop();
    };
  }, [pathname]);
}

/** Mirrors focus mode onto <html> so CSS outside the shell (toasts, overscroll, the chrome tokens) can follow it. */
function useFocusModeClass(active: boolean) {
  useEffect(() => {
    document.documentElement.classList.toggle('focus-mode', active);
    return () => document.documentElement.classList.remove('focus-mode');
  }, [active]);
}

/** Icon sizes are set per breakpoint in styles.css. `tooltips`: name each item in a tooltip while the rail is collapsed. */
function NavItems({ tooltips = false }: { tooltips?: boolean }) {
  const ai = useAiConfig() != null;
  return NAV.filter((n) => ai || !n.ai).map((n) => (
    <NavLink
      key={n.to}
      to={n.to}
      end={n.end}
      className={({ isActive }) => cx('nav-item', n.cta && 'brew-cta', n.notPhone && 'not-phone', isActive && 'active')}
      data-tooltip={tooltips ? n.label : undefined}
    >
      <span className="nav-icon">
        <Icon name={n.icon} size={24} />
      </span>
      <span className="nav-label">{n.label}</span>
    </NavLink>
  ));
}

/** The toggle's tooltip always shows (it has no label); an item's only while the rail is collapsed to icons. */
function railTooltipEnabled(el: HTMLElement): boolean {
  if (el.classList.contains('nav-toggle')) return true;
  return document.documentElement.classList.contains('nav-collapsed') && window.matchMedia(MQ.collapsibleNav).matches;
}

export function Layout() {
  useScrollToTopOnNavigate();
  useRouteTitle();
  usePageViews();
  useFocusPageOnNavigate();
  const focus = useFocusModeActive();
  useFocusModeClass(focus);
  const [navCollapsed, setNavCollapsed] = useState(readNavCollapsed);
  const rail = useRef<HTMLElement>(null);
  const toggleLabel = navCollapsed ? 'Expand menu' : 'Collapse menu';
  const ai = useAiConfig() != null;

  return (
    <div className={cx('app-shell', focus && 'focus')}>
      {/* Past the top bar and the navigation for keyboard users. Focus moves without touching the URL's hash. */}
      <a
        href="#main"
        className="skip-link"
        onClick={(e) => {
          e.preventDefault();
          document.getElementById('main')?.focus();
        }}
      >
        Skip to content
      </a>
      {!focus && (
        <header className="topbar">
          <Link to="/" className="brand">
            <Logo height={27} title="Taretto" />
          </Link>
          <div className="spacer" />
          <ScaleStatusPill />
          <MachineStatusPill />
          {ai && (
            <NavLink to="/ask" className="btn ghost icon topbar-ask" aria-label="Ask Taretto">
              <Icon name="sparkles" />
            </NavLink>
          )}
          {/* Shows where you are on phones, where Settings has no tab of its own (styles.css). */}
          <NavLink to="/settings" className="btn ghost icon topbar-settings" aria-label="Settings">
            <Icon name="settings" />
          </NavLink>
        </header>
      )}

      {!focus && (
        <nav className="nav-rail" id="nav-rail" aria-label="Main" ref={rail}>
          <NavItems tooltips />
          {/* Shown with a mouse from 1440px (MQ.collapsibleNav). Focus stays on it, at the rail's foot either way. */}
          <button
            type="button"
            className="nav-toggle"
            aria-label={toggleLabel}
            aria-expanded={!navCollapsed}
            aria-controls="nav-rail"
            data-tooltip={toggleLabel}
            onClick={() => {
              // The class first, so the tooltip measures the new width when it places itself again.
              applyNavCollapsed(!navCollapsed);
              setNavCollapsed(!navCollapsed);
            }}
          >
            <span className="nav-icon">
              <Icon name={navCollapsed ? 'chevron-right' : 'chevron-left'} size={24} />
            </span>
          </button>
        </nav>
      )}
      {!focus && <NavTooltip rail={rail} enabled={railTooltipEnabled} refresh={navCollapsed} />}

      <main id="main" className="main" tabIndex={-1}>
        <Outlet />
      </main>

      {!focus && (
        <nav className="bottom-nav" aria-label="Main">
          <NavItems />
        </nav>
      )}
    </div>
  );
}
